
import { encryptData, decryptData } from './encryption';
import { persistentStore } from './LocalPersistentStore';

export const safeSetItem = async (key: string, value: string): Promise<boolean> => {
  try {
    const encryptedValue = encryptData(value);

    // 1. Try to save to localStorage as a fast-cache (if it fits)
    try {
      localStorage.setItem(key, encryptedValue);
    } catch (e) {
      // If it fails, that's fine, we have IndexedDB
    }

    // 2. Save the version to IndexedDB
    // persistentStore.setJson handles its own internal encryption, so we pass the PARSED value here.
    try {
      const parsed = JSON.parse(value);
      await persistentStore.setJson('SAVES', key, parsed);
    } catch (e) {
      // If it's not JSON, just save it as is (it will be encrypted by setJson)
      await persistentStore.setJson('SAVES', key, value);
    }
    
    return true;
  } catch (e) {
    console.error(`Critical failure saving key: ${key}`, e);
    return false;
  }
};

export const safeGetItem = (key: string): string | null => {
  try {
    const value = localStorage.getItem(key);
    if (!value) return null;
    
    // Check if it's already encrypted (likely)
    if (!value.startsWith('{') && !value.startsWith('[')) {
      return decryptData(value);
    }
    return value;
  } catch (e) {
    return null;
  }
};

export const safeGetItemAsync = async (key: string): Promise<string | null> => {
  try {
    // 1. Try localStorage first
    const local = localStorage.getItem(key);
    if (local) {
      if (!local.startsWith('{') && !local.startsWith('[')) {
        return decryptData(local);
      }
      return local;
    }

    // 2. Try IndexedDB
    // getJson handles its own internal decryption and returns the original object/data
    const idbValue = await persistentStore.getJson<any>('SAVES', key);
    if (idbValue) {
      if (typeof idbValue === 'string') return idbValue;
      return JSON.stringify(idbValue);
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const safeRemoveItem = async (key: string): Promise<void> => {
  try {
    localStorage.removeItem(key);
    await persistentStore.delete('SAVES', key);
  } catch (e) {
    console.error(`Failed to remove item from storage: ${key}`, e);
  }
};

/**
 * Attempts to save a config object to IndexedDB.
 */
export const safeSaveConfig = async (key: string, config: any): Promise<boolean> => {
  try {
    // persistentStore.setJson handles internal encryption
    await persistentStore.setJson('SAVES', key, config);
    
    // Optional: Also try localStorage for quick sync loads
    try {
      const configString = JSON.stringify(config);
      localStorage.setItem(key, encryptData(configString));
    } catch (e) {
      // Silent fail for localStorage, it's just a cache now
    }
    
    return true;
  } catch (e) {
    console.error("Critical failure: Could not save config to IndexedDB", e);
    return false;
  }
};
